"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Box, Button, Container, Divider, Grid, Stack, Typography, useTheme, IconButton } from "@mui/material";
import { X, LinkedIn, YouTube, Instagram } from "@mui/icons-material";

const footerLinks = [
	{ title: "Mission", href: "/about/mission" },
	{ title: "Features", href: "/features" },
	{ title: "Pricing", href: "/pricing" },
	{ title: "FAQ", href: "/faq" },
	{ title: "Job Tracker", href: "/job-application-tracker" },
	{ title: "Resume Builder", href: "/resume-builder" },
	{ title: "Recruiting Companies", href: "/recruiting-companies" },
	{ title: "Blogs", href: "/blogs" },
	{ title: "Contact Us", href: "/contact-us" },
];

const legalLinks = [
	{ title: "Privacy Policy", href: "/privacy-policy" },
	{ title: "Terms & Conditions", href: "/terms-conditions" },
	{ title: "Cancellation Policy", href: "/cancellation-policy" },
	{ title: "Sitemap", href: "/sitemap" },
];

const socialLinks = [
	{ icon: <LinkedIn />, url: "https://www.linkedin.com/company/jobstacky/", label: "LinkedIn — JobStacky" },
	{ icon: <LinkedIn />, url: "https://www.linkedin.com/company/stackyhub/", label: "LinkedIn — StackyHub" },
	{ icon: <X />, url: "https://x.com/JobStacky", label: "X" },
	{ icon: <YouTube />, url: "https://www.youtube.com/@JobStacky", label: "YouTube" },
	{ icon: <Instagram />, url: "https://www.instagram.com/jobstacky/", label: "Instagram" },
	{ icon: <Box component="img" src="https://cdn.simpleicons.org/tiktok/FFFFFF" alt="" sx={{ width: 22, height: 22 }} />, url: "https://www.tiktok.com/@jobstacky", label: "TikTok" },
];

const socialButtonSx = {
	color: "inherit",
	bgcolor: "rgba(255, 255, 255, 0.2)",
	"&:hover": {
		bgcolor: "rgba(255, 255, 255, 0.3)",
		transform: "translateY(-2px)",
	},
	transition: "all 0.3s ease",
	width: 40,
	height: 40,
};

export function ExpandedFooter() {
	const theme = useTheme();
	const buildNumber = process.env.NEXT_PUBLIC_BUILD_NUMBER;
	const environment = process.env.NEXT_PUBLIC_ENVIRONMENT;

	const footerBgColor = theme.palette.mode === 'dark' ? '#1a1a1a' : '#0f766e';

	return (
		<Box component="footer" sx={{ bgcolor: footerBgColor, color: "#fff", mt: "auto" }}>
			<Container maxWidth="lg" sx={{ py: 6 }}>
				<Grid container spacing={4}>
					<Grid item xs={12} md={5}>
						<Link href="/" aria-label="JobStacky home" style={{ display: "inline-flex", marginBottom: 16 }}>
							<Image
								src="/images/logo_white.png"
								alt="JobStacky"
								width={160}
								height={43}
								style={{ objectFit: "contain" }}
							/>
						</Link>
						<Typography sx={{ color: "rgba(255,255,255,0.96)", lineHeight: 1.8, maxWidth: 520 }}>
							JobStacky is the professional workspace for job tracking, referrals, company status, resumes, and cover letters.
						</Typography>
						<Stack direction="row" spacing={1} sx={{ mt: 2 }}>
							{socialLinks.map((social) => (
								<IconButton key={social.label} component="a" href={social.url} target="_blank" rel="noopener noreferrer" sx={socialButtonSx} aria-label={social.label}>
									{social.icon}
								</IconButton>
							))}
						</Stack>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="h5" component="h3" fontWeight={800} sx={{ mb: 2 }}>
							Product
						</Typography>
						<Stack spacing={1.2}>
							{footerLinks.map((link) => (
								<Button key={link.title} component={Link} href={link.href} sx={{ justifyContent: "flex-start", color: "#fff", px: 0, fontWeight: 600 }}>
									{link.title}
								</Button>
							))}
						</Stack>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Typography variant="h5" component="h3" fontWeight={800} sx={{ mb: 2 }}>
							Legal
						</Typography>
						<Stack spacing={1.2}>
							{legalLinks.map((link) => (
								<Button key={link.title} component={Link} href={link.href} sx={{ justifyContent: "flex-start", color: "#fff", px: 0, fontWeight: 600 }}>
									{link.title}
								</Button>
							))}
						</Stack>
					</Grid>
				</Grid>

				<Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.15)" }} />

				<Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
					<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.92)" }}>
						Copyright JobStacky - a part of StackyHub LLP, All Rights Reserved
					</Typography>
					<Typography variant="caption" sx={{ color: "#fff", fontFamily: "monospace" }}>
						Build: {buildNumber ? `${buildNumber} - ${environment}` : "Local"}
					</Typography>
				</Stack>
			</Container>
		</Box>
	);
}

export function MiniFooter() {
	const theme = useTheme();
	const currentYear = new Date().getFullYear();

	const footerLinks = [
		{ title: "Features", href: "/features" },
		{ title: "Recruiting Companies", href: "/recruiting-companies" },
		{ title: "Pricing", href: "/pricing" },
		{ title: "Blogs", href: "/blogs" },
		{ title: "Contact Us", href: "/contact-us" },
		{ title: "Privacy Policy", href: "/privacy-policy" },
		{ title: "Terms & Conditions", href: "/terms-conditions" },
		{ title: "Cancellation Policy", href: "/cancellation-policy" },
		{ title: "Sitemap", href: "/sitemap" },
	];

	return (
		<Box
			component="footer"
			sx={{
				bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#0f766e',
				color: "#fff",
				textAlign: "center",
				p: 2.5,
			}}
		>
			<Box sx={{ width: "100%" }}>
				{/* Desktop/Tablet View - Single Line */}
				<Stack
					direction={{ xs: "column", md: "row" }}
					justifyContent="space-between"
					alignItems="center"
					spacing={{ xs: 3, md: 2 }}
					sx={{ display: { xs: "none", md: "flex" } }}
				>
					<Typography variant="body2" color="inherit">
						© Copyright {currentYear} Jobstacky a part of StackyHub LLP, All Rights Reserved
					</Typography>

					{/* Footer Links - Desktop */}
					<Stack direction="row" spacing={0}>
						{footerLinks.map((link, index) => (
							<React.Fragment key={index}>
								{index > 0 && <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />}
								<Button
									component={Link}
									href={link.href}
									sx={{
										color: "#fff",
										px: 1,
										fontSize: "0.875rem",
										fontWeight: 600,
										"&:hover": {
											textDecoration: "underline",
											textUnderlineOffset: "4px",
										},
									}}
								>
									{link.title}
								</Button>
							</React.Fragment>
						))}
					</Stack>

					{/* Social Icons - Right aligned on desktop (only show if socialLinks exist) */}
					{socialLinks.length > 0 && (
						<Stack direction="row" spacing={1}>
							{socialLinks.map((social, index) => (
								<IconButton
									key={index}
									component={Link}
									href={social.url || "#"}
									target="_blank"
									rel="noopener noreferrer"
									disabled={!social.url}
									sx={socialButtonSx}
									aria-label={social.label}
								>
									{social.icon}
									<span
										style={{
											position: "absolute",
											width: "1px",
											height: "1px",
											padding: "0",
											margin: "-1px",
											overflow: "hidden",
											clip: "rect(0, 0, 0, 0)",
											whiteSpace: "nowrap",
											borderWidth: "0",
										}}
									>
										{social.label}
									</span>
								</IconButton>
							))}
						</Stack>
					)}
				</Stack>

				{/* Mobile View - Stacked */}
				<Stack direction="column" spacing={3} alignItems="center" sx={{ display: { xs: "flex", md: "none" } }}>
					<Typography variant="body2" color="inherit">
						© Copyright {currentYear}, All Rights Reserved
					</Typography>

					{/* Social Icons - Mobile (only show if socialLinks exist) */}
					{socialLinks.length > 0 && (
						<Stack direction="row" spacing={1}>
							{socialLinks.map((social, index) => (
								<IconButton
									key={index}
									component={Link}
									href={social.url || "#"}
									target="_blank"
									rel="noopener noreferrer"
									disabled={!social.url}
									sx={socialButtonSx}
									aria-label={social.label}
								>
									{social.icon}
									<span
										style={{
											position: "absolute",
											width: "1px",
											height: "1px",
											padding: "0",
											margin: "-1px",
											overflow: "hidden",
											clip: "rect(0, 0, 0, 0)",
											whiteSpace: "nowrap",
											borderWidth: "0",
										}}
									>
										{social.label}
									</span>
								</IconButton>
							))}
						</Stack>
					)}

					{/* Footer Links - Mobile Grid (2x3 layout) */}
					<Grid container spacing={1} justifyContent="center" sx={{ width: "100%" }}>
						{footerLinks.map((link, index) => (
							<Grid item xs={6} sm={4} key={index} sx={{ textAlign: "center" }}>
								<Button
									component={Link}
									href={link.href}
									sx={{
										color: "#fff",
										fontSize: "0.75rem",
										fontWeight: 600,
										py: 1,
										px: 1,
										width: "100%",
										"&:hover": {
											textDecoration: "underline",
											textUnderlineOffset: "4px",
											bgcolor: "rgba(255, 255, 255, 0.1)",
										},
										transition: "all 0.2s ease",
									}}
								>
									{link.title}
								</Button>
							</Grid>
						))}
					</Grid>
				</Stack>
			</Box>
		</Box>
	);
}
